﻿/**
 * Lightweight reactive state store.
 * Replaces Knockout observables with a proxy-based approach.
 * Components subscribe to state changes and re-render only when relevant state changes.
 */
export type Listener<T> = (state: T, changedKey: keyof T) => void;

export class ReactiveStore<T extends Record<string, any>> {
    private listeners: Set<Listener<T>> = new Set();
    private state: T;
    proxy: T;

    constructor(initialState: T) {
        this.state = { ...initialState };
        this.proxy = new Proxy(this.state, {
            set: (target, key: string, value) => {
                const old = target[key as keyof T];
                if (old === value) return true;
                (target as any)[key] = value;
                this.notify(key as keyof T);
                return true;
            },
            get: (target, key: string) => {
                return target[key as keyof T];
            },
        });
    }

    subscribe(listener: Listener<T>): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify(changedKey: keyof T): void {
        this.listeners.forEach((fn) => fn(this.state, changedKey));
    }

    /** Batch multiple state changes into a single notification */
    batch(updater: (state: T) => void): void {
        const keys: Set<keyof T> = new Set();
        const snapshot: Partial<T> = {};
        const batchProxy = new Proxy({} as T, {
            set: (_target, key: string, value) => {
                keys.add(key as keyof T);
                (snapshot as any)[key] = value;
                return true;
            },
        });
        updater(batchProxy);
        // Apply collected changes through the real proxy so notifications fire
        keys.forEach((key) => {
            (this.proxy as any)[key] = snapshot[key];
        });
    }
}